import { useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { subscriptionToRow } from '../lib/pushHelpers'

// Auto-heal stale push subscriptions on cold start.
//
// Apple sometimes silently rotates / invalidates a PWA's APNs token —
// the server keeps getting 2xx on push attempts but the device never
// receives them. There's no API to detect this from the server side;
// the only signal is that the device's current
// `pushManager.getSubscription()` endpoint no longer matches the row
// we have in the DB.
//
// On every mount where the user is authenticated and push permission
// is granted, this hook:
//   1. Reads the device's current push subscription
//   2. Compares its endpoint against what's in push_subscriptions
//   3. If the endpoint isn't recorded for this user, upserts the new
//      one and deletes any stale endpoints for the same user (the
//      old zombie that Apple silently dropped)
//
// Costs: one DB select + at most one upsert + one delete, per
// session. Silent on the happy path.

function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function usePushAutoRefresh() {
  const { user } = useAuth()

  useEffect(() => {
    if (!user) return
    if (typeof navigator === 'undefined') return
    if (!('serviceWorker' in navigator)) return
    if (!('PushManager' in window)) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return

    let cancelled = false
    ;(async () => {
      try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        if (cancelled || !sub) return

        // Endpoint already known? Then we're up to date.
        const { data: existing } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('endpoint', sub.endpoint)
          .maybeSingle()
        if (cancelled || existing) return

        // New endpoint — Apple rotated the token. Persist + prune.
        const row = subscriptionToRow(sub)
        await supabase
          .from('push_subscriptions')
          .upsert({
            user_id: user.id,
            ...row,
            enabled: true,
            timezone: deviceTimezone(),
            updated_at: new Date().toISOString(),
          }, { onConflict: 'user_id,endpoint' })
        if (cancelled) return
        // Prune any other rows for this user whose endpoint doesn't
        // match the current device — those are zombies Apple already
        // silently dropped. Keeping them around just bloats the
        // dispatch loop with no-op sends.
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .neq('endpoint', sub.endpoint)
      } catch {
        // Best-effort; silent on failure. The user's notifications
        // either work or they don't — no need to surface a warning.
      }
    })()

    return () => { cancelled = true }
  }, [user])
}
